import { useEffect, useState, useMemo, Fragment } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Loader2, Search, RefreshCw, ChevronDown, ChevronRight, PlayCircle, AlertTriangle, CheckCircle2, Globe, Trash2 } from "lucide-react";
import { toast } from "sonner";

const FEED_TYPE_BADGE = {
  json_ld: "bg-emerald-100 text-emerald-900",
  microdata: "bg-emerald-100 text-emerald-900",
  embedded_json: "bg-sky-100 text-sky-900",
  ats_workday: "bg-violet-100 text-violet-900",
  ats_greenhouse: "bg-violet-100 text-violet-900",
  ats_lever: "bg-violet-100 text-violet-900",
  ats_smartrecruiters: "bg-violet-100 text-violet-900",
  ats_workable: "bg-violet-100 text-violet-900",
  wp_ajax: "bg-amber-100 text-amber-900",
  html_anchors: "bg-stone-200 text-stone-800",
};

function FeedTypeBadge({ type }) {
  if (!type) return <span className="text-xs text-muted-foreground">—</span>;
  const cls = FEED_TYPE_BADGE[type] || "bg-stone-200 text-stone-800";
  return (
    <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold ${cls}`}>
      {type.replace("_", " ")}
    </span>
  );
}

function StatusPill({ status }) {
  if (status == null) return <span className="text-xs text-muted-foreground">—</span>;
  const ok = typeof status === "number" ? status >= 200 && status < 400 : false;
  return (
    <span className={`font-mono text-[11px] px-1.5 py-0.5 rounded ${ok ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-800"}`}>
      {status}
    </span>
  );
}

export default function FeedDebugPanel() {
  // Probe form state
  const [probeUrl, setProbeUrl] = useState("https://www.gbrailfreight.com/careers/");
  const [probeHint, setProbeHint] = useState("GB Railfreight");
  const [probeDryRun, setProbeDryRun] = useState(true);
  const [probeRunning, setProbeRunning] = useState(false);
  const [probeResult, setProbeResult] = useState(null);

  // Known operators sweep
  const [operators, setOperators] = useState([]);
  const [sweepOnly, setSweepOnly] = useState([]);     // names selected
  const [sweepDryRun, setSweepDryRun] = useState(false);
  const [sweepRunning, setSweepRunning] = useState(false);
  const [sweepResult, setSweepResult] = useState(null);

  // Run history
  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [expandedRun, setExpandedRun] = useState(null);

  // Cleanup
  const [cleanupRunning, setCleanupRunning] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);
  const [vacancyCleanupRunning, setVacancyCleanupRunning] = useState(false);
  const [vacancyCleanupResult, setVacancyCleanupResult] = useState(null);
  const [vacancyCleanupBrokenLinks, setVacancyCleanupBrokenLinks] = useState(false);

  // Filters for runs
  const [filterType, setFilterType] = useState("all");
  const [filterText, setFilterText] = useState("");

  const loadOps = async () => {
    try {
      const r = await api.get("/admin/feed/known-operators");
      setOperators(r.data.operators || []);
    } catch { /* ignore */ }
  };
  const loadRuns = async () => {
    setRunsLoading(true);
    try {
      const r = await api.get("/admin/feed/runs?limit=50");
      setRuns(r.data.runs || []);
    } catch (e) {
      toast.error("Failed to load runs");
    } finally {
      setRunsLoading(false);
    }
  };

  useEffect(() => { loadOps(); loadRuns(); }, []);

  const runProbe = async () => {
    if (!probeUrl) { toast.error("Paste a careers URL"); return; }
    setProbeRunning(true); setProbeResult(null);
    try {
      const r = await api.post("/admin/feed/probe", {
        careers_url: probeUrl,
        employer_hint: probeHint || null,
        dry_run: probeDryRun,
        max_jobs: 80,
      });
      setProbeResult(r.data);
      if (r.data.inserted > 0) toast.success(`Probe → ${r.data.inserted} new vacancies inserted`);
      else if (r.data.matched > 0) toast.success(`Probe → ${r.data.matched} jobs detected (dry-run or all duplicates)`);
      else toast.message(`Probe → 0 jobs found via ${r.data.feed_type || "any layer"}`);
      loadRuns();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Probe failed");
    } finally { setProbeRunning(false); }
  };

  const runSweep = async () => {
    setSweepRunning(true); setSweepResult(null);
    try {
      const payload = { dry_run: sweepDryRun, max_jobs: 60 };
      if (sweepOnly.length > 0) payload.only = sweepOnly;
      const r = await api.post("/admin/feed/sync-known", payload);
      setSweepResult(r.data);
      const t = r.data.totals || {};
      toast.success(`Sweep done — ${t.inserted || 0} inserted, ${t.found || 0} found across ${r.data.operators.length} operators`);
      loadRuns();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sweep failed");
    } finally { setSweepRunning(false); }
  };

  const toggleOp = (name) => {
    setSweepOnly((s) => s.includes(name) ? s.filter((n) => n !== name) : [...s, name]);
  };

  const runCleanup = async (dryRun) => {
    if (!dryRun && !window.confirm("Permanently delete every layered-crawler vacancy whose URL or title now fails the tightened filters? Real job postings are preserved.")) {
      return;
    }
    setCleanupRunning(true); setCleanupResult(null);
    try {
      const r = await api.post("/admin/feed/cleanup", { feed_type: "html_anchors", dry_run: dryRun });
      setCleanupResult(r.data);
      if (dryRun) toast.message(`Preview: ${r.data.would_delete} of ${r.data.scanned} would be deleted`);
      else toast.success(`Cleaned up ${r.data.deleted} junk vacancies (${r.data.kept} kept)`);
      loadRuns();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cleanup failed");
    } finally { setCleanupRunning(false); }
  };

  const runVacancyCleanup = async (dryRun) => {
    if (!dryRun && !window.confirm("Permanently delete live vacancies that are expired or have invalid/broken application links?")) {
      return;
    }
    setVacancyCleanupRunning(true); setVacancyCleanupResult(null);
    try {
      const r = await api.post("/admin/vacancies/cleanup", {
        dry_run: dryRun,
        check_broken_links: vacancyCleanupBrokenLinks,
      });
      setVacancyCleanupResult(r.data);
      if (dryRun) {
        toast.message(`Preview: ${r.data.would_delete} of ${r.data.scanned} would be deleted`);
      } else {
        toast.success(`Cleaned up ${r.data.deleted} vacancies (${r.data.kept} kept)`);
      }
      loadRuns();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Vacancy cleanup failed");
    } finally { setVacancyCleanupRunning(false); }
  };

  const filteredRuns = useMemo(() => {
    return runs.filter((r) => {
      if (filterType !== "all" && r.feed_type !== filterType) return false;
      if (filterText) {
        const t = filterText.toLowerCase();
        if (!(r.careers_url || "").toLowerCase().includes(t) && !(r.employer_hint || "").toLowerCase().includes(t)) return false;
      }
      return true;
    });
  }, [runs, filterType, filterText]);

  const distinctFeedTypes = useMemo(() => {
    const s = new Set();
    runs.forEach((r) => { if (r.feed_type) s.add(r.feed_type); });
    return Array.from(s).sort();
  }, [runs]);

  return (
    <div className="space-y-6" data-testid="feed-debug-panel">
      {/* === Single URL probe === */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-md bg-sky-100 text-sky-900 flex items-center justify-center"><Search className="w-5 h-5" /></div>
          <div>
            <div className="font-display font-bold text-ink text-xl tracking-tight">Probe a single careers URL</div>
            <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
              Runs the layered detector — tries JSON-LD → Microdata → Embedded JSON (e.g. <code>careersData.eploy</code>) → ATS APIs (Workday, Greenhouse, Lever, SmartRecruiters, Workable) → WP AJAX <code>admin-ajax.php</code> → HTML anchor fallback. Returns a full diagnostic showing exactly which layer succeeded and why others were skipped.
            </p>
          </div>
        </div>
        <div className="mt-5 grid md:grid-cols-[1fr_280px] gap-4">
          <div>
            <Label>Careers URL</Label>
            <Input
              data-testid="probe-url"
              className="mt-1 font-mono text-sm"
              value={probeUrl}
              onChange={(e) => setProbeUrl(e.target.value)}
              placeholder="https://www.gbrailfreight.com/careers/"
            />
          </div>
          <div>
            <Label>Employer name (fallback)</Label>
            <Input
              data-testid="probe-hint"
              className="mt-1"
              value={probeHint}
              onChange={(e) => setProbeHint(e.target.value)}
              placeholder="GB Railfreight"
            />
          </div>
        </div>
        <div className="mt-4 flex items-center gap-4 flex-wrap">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={probeDryRun} onChange={(e) => setProbeDryRun(e.target.checked)} data-testid="probe-dryrun" />
            <span>Dry-run (don't insert)</span>
          </label>
          <Button onClick={runProbe} disabled={probeRunning} className="bg-navy text-bone hover:bg-lime-600 hover:text-ink font-semibold" data-testid="probe-run">
            {probeRunning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-2" />}
            {probeRunning ? "Probing…" : "Run probe"}
          </Button>
        </div>
        {probeResult && (
          <div className="mt-5 space-y-4" data-testid="probe-result">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[
                { l: "HTTP", v: probeResult.http_status ?? "—" },
                { l: "Found", v: probeResult.found },
                { l: "Matched", v: probeResult.matched },
                { l: "Inserted", v: probeResult.inserted },
                { l: "Skipped", v: probeResult.skipped },
              ].map((s) => (
                <div key={s.l} className="bg-paper border border-border rounded p-3">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{s.l}</div>
                  <div className="font-mono font-bold text-ink text-2xl mt-1">{s.v}</div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-muted-foreground">Feed type:</span>
              <FeedTypeBadge type={probeResult.feed_type} />
              <span className="text-muted-foreground ml-3">Content-Type:</span>
              <span className="font-mono text-xs">{probeResult.content_type || "—"}</span>
            </div>

            <div className="bg-paper border border-border rounded-lg p-4">
              <div className="font-semibold text-ink text-sm mb-2">Detection layers tried</div>
              <table className="w-full text-xs">
                <thead className="text-muted-foreground">
                  <tr className="text-left">
                    <th className="py-1.5 pr-3">Layer</th>
                    <th className="py-1.5 pr-3">Status</th>
                    <th className="py-1.5 pr-3">Count</th>
                    <th className="py-1.5">Detail</th>
                  </tr>
                </thead>
                <tbody className="font-mono">
                  {(probeResult.layers || []).map((l, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="py-1.5 pr-3 text-ink font-semibold">{l.name}</td>
                      <td className="py-1.5 pr-3"><StatusPill status={l.status} /></td>
                      <td className="py-1.5 pr-3">{l.count ?? 0}</td>
                      <td className="py-1.5 text-muted-foreground break-all">
                        {l.endpoint && <span>endpoint: {l.endpoint} </span>}
                        {l.matched_pattern && <span>pattern: {l.matched_pattern} </span>}
                        {l.matched_action && <span>action: {l.matched_action} </span>}
                        {l.error && <span className="text-rose-700">error: {l.error}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {probeResult.candidates && probeResult.candidates.length > 0 && (
              <div className="bg-paper border border-border rounded-lg p-4">
                <div className="font-semibold text-ink text-sm mb-2">Candidates ({probeResult.candidates.length}) <span className="font-normal text-muted-foreground">— dry-run preview</span></div>
                <ul className="space-y-1 text-xs font-mono">
                  {probeResult.candidates.slice(0, 25).map((c, i) => (
                    <li key={i} className="flex flex-wrap gap-x-2">
                      <span className="text-ink font-semibold">{c.title}</span>
                      <span className="text-muted-foreground">@ {c.location || "—"}</span>
                      <a href={c.apply_url} target="_blank" rel="noreferrer" className="text-sky-700 hover:underline break-all">→ {c.apply_url}</a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {Object.keys(probeResult.skip_reasons || {}).length > 0 && (
              <div className="bg-paper border border-border rounded-lg p-4">
                <div className="font-semibold text-ink text-sm mb-2 flex items-center gap-2"><AlertTriangle className="w-3.5 h-3.5 text-amber-600" /> Skip reasons ({Object.keys(probeResult.skip_reasons).length})</div>
                <ul className="space-y-1 text-xs font-mono max-h-64 overflow-y-auto">
                  {Object.entries(probeResult.skip_reasons).slice(0, 50).map(([k, v]) => (
                    <li key={k} className="break-all"><span className="text-rose-700">{v}</span> — <span className="text-muted-foreground">{k}</span></li>
                  ))}
                </ul>
              </div>
            )}

            {(probeResult.errors || []).length > 0 && (
              <div className="bg-rose-50 border border-rose-200 rounded-lg p-4 text-xs text-rose-900">
                <div className="font-semibold mb-1">Errors</div>
                <ul className="space-y-1 font-mono">
                  {probeResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* === Known operators sweep === */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-md bg-amber/20 text-ink flex items-center justify-center"><Globe className="w-5 h-5" /></div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div className="font-display font-bold text-ink text-xl tracking-tight">Run all known UK rail operators</div>
              <span className="text-xs text-muted-foreground">{operators.length} operators · runs nightly at 03:00 UK</span>
            </div>
            <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
              Sweeps every TOC + FOC + infrastructure operator using the layered detector. Persists each run's diagnostic. Tick operators below to limit the sweep, or leave all unticked to run the full list.
            </p>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          {operators.map((op) => (
            <label key={op.name} className="flex items-center gap-2 cursor-pointer p-2 rounded border border-border hover:bg-paper" data-testid={`op-toggle-${op.name.replace(/\s+/g, "-").toLowerCase()}`}>
              <input
                type="checkbox"
                checked={sweepOnly.includes(op.name)}
                onChange={() => toggleOp(op.name)}
              />
              <span className="text-ink truncate">{op.name}</span>
            </label>
          ))}
        </div>

        <div className="mt-4 flex items-center gap-4 flex-wrap">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={sweepDryRun} onChange={(e) => setSweepDryRun(e.target.checked)} data-testid="sweep-dryrun" />
            <span>Dry-run (don't insert)</span>
          </label>
          {sweepOnly.length > 0 && (
            <Button size="sm" variant="ghost" onClick={() => setSweepOnly([])} className="text-xs">Clear selection ({sweepOnly.length})</Button>
          )}
          <Button onClick={runSweep} disabled={sweepRunning} className="bg-navy text-bone hover:bg-lime-600 hover:text-ink font-semibold ml-auto" data-testid="sweep-run">
            {sweepRunning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-2" />}
            {sweepRunning ? "Sweeping…" : sweepOnly.length > 0 ? `Run sweep (${sweepOnly.length} selected)` : "Run sweep (all operators)"}
          </Button>
        </div>

        {sweepResult && (
          <div className="mt-5 space-y-3" data-testid="sweep-result">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { l: "Found", v: sweepResult.totals?.found ?? 0 },
                { l: "Matched", v: sweepResult.totals?.matched ?? 0 },
                { l: "Inserted", v: sweepResult.totals?.inserted ?? 0 },
                { l: "Skipped", v: sweepResult.totals?.skipped ?? 0 },
              ].map((s) => (
                <div key={s.l} className="bg-paper border border-border rounded p-3">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{s.l}</div>
                  <div className="font-mono font-bold text-ink text-2xl mt-1">{s.v}</div>
                </div>
              ))}
            </div>
            <div className="bg-paper border border-border rounded-lg overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-sand text-muted-foreground">
                  <tr className="text-left">
                    <th className="py-2 px-3">Operator</th>
                    <th className="py-2 px-3">Feed type</th>
                    <th className="py-2 px-3">HTTP</th>
                    <th className="py-2 px-3">Found</th>
                    <th className="py-2 px-3">Inserted</th>
                    <th className="py-2 px-3">Skipped</th>
                  </tr>
                </thead>
                <tbody>
                  {sweepResult.operators.map((o, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="py-2 px-3 text-ink font-semibold">{o.name}</td>
                      <td className="py-2 px-3"><FeedTypeBadge type={o.feed_type} /></td>
                      <td className="py-2 px-3"><StatusPill status={o.http_status} /></td>
                      <td className="py-2 px-3 font-mono">{o.found}</td>
                      <td className="py-2 px-3 font-mono">{o.inserted}</td>
                      <td className="py-2 px-3 font-mono">{o.skipped}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* === Cleanup garbage from old crawls === */}
      <div className="bg-card border border-amber-300 rounded-lg p-6">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-md bg-rose-100 text-rose-900 flex items-center justify-center"><AlertTriangle className="w-5 h-5" /></div>
          <div className="flex-1">
            <div className="font-display font-bold text-ink text-xl tracking-tight">Cleanup junk from previous crawls</div>
            <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
              Earlier versions of the layered crawler over-collected nav/marketing links from sites without a real job feed (e.g. "Cookie policy", "About us", "Read More"). Run a dry-run preview, then delete. Only vacancies with <code className="bg-paper px-1 rounded">source=layered-crawl</code> and <code className="bg-paper px-1 rounded">source_feed_type=html_anchors</code> are scanned — real Adzuna / sitemap / embedded-JSON imports are untouched.
            </p>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <Button onClick={() => runCleanup(true)} disabled={cleanupRunning} variant="outline" data-testid="cleanup-preview">
            {cleanupRunning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
            Preview cleanup (dry-run)
          </Button>
          <Button onClick={() => runCleanup(false)} disabled={cleanupRunning || !cleanupResult || cleanupResult.would_delete === 0} className="bg-rose-700 text-bone hover:bg-rose-800 font-semibold" data-testid="cleanup-run">
            {cleanupRunning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
            {cleanupResult ? `Delete ${cleanupResult.would_delete} junk vacancies` : "Delete junk vacancies"}
          </Button>
        </div>
        {cleanupResult && (
          <div className="mt-5 space-y-3" data-testid="cleanup-result">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { l: "Scanned", v: cleanupResult.scanned },
                { l: "Would delete", v: cleanupResult.would_delete },
                { l: "Kept", v: cleanupResult.kept },
                { l: "Actually deleted", v: cleanupResult.deleted },
              ].map((s) => (
                <div key={s.l} className="bg-paper border border-border rounded p-3">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{s.l}</div>
                  <div className="font-mono font-bold text-ink text-2xl mt-1">{s.v}</div>
                </div>
              ))}
            </div>
            {cleanupResult.preview && cleanupResult.preview.length > 0 && (
              <div className="bg-paper border border-border rounded-lg p-4">
                <div className="font-semibold text-ink text-sm mb-2">Junk preview ({cleanupResult.preview.length})</div>
                <ul className="text-xs font-mono space-y-1 max-h-64 overflow-y-auto">
                  {cleanupResult.preview.map((p, i) => (
                    <li key={i} className="flex flex-wrap gap-x-2 break-all">
                      <span className="text-rose-700">×</span>
                      <span className="text-ink">{p.title}</span>
                      <span className="text-muted-foreground">— {p.employer_name} — </span>
                      <span className="text-muted-foreground">{p.apply_url}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="bg-card border border-amber-300 rounded-lg p-6">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-md bg-sky-100 text-sky-900 flex items-center justify-center"><AlertTriangle className="w-5 h-5" /></div>
          <div className="flex-1">
            <div className="font-display font-bold text-ink text-xl tracking-tight">Cleanup expired or broken live vacancies</div>
            <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
              Delete live vacancies that are past their expiry date or that do not have a valid application URL. Enable broken-link probing to also check whether the apply URL resolves successfully.
            </p>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={vacancyCleanupBrokenLinks} onChange={(e) => setVacancyCleanupBrokenLinks(e.target.checked)} />
            <span>Also probe links for HTTP success</span>
          </label>
          <Button onClick={() => runVacancyCleanup(true)} disabled={vacancyCleanupRunning} variant="outline" data-testid="vacancy-cleanup-preview">
            {vacancyCleanupRunning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
            Preview vacancy cleanup (dry-run)
          </Button>
          <Button onClick={() => runVacancyCleanup(false)} disabled={vacancyCleanupRunning || !vacancyCleanupResult || vacancyCleanupResult.would_delete === 0} className="bg-rose-700 text-bone hover:bg-rose-800 font-semibold" data-testid="vacancy-cleanup-run">
            {vacancyCleanupRunning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
            {vacancyCleanupResult ? `Delete ${vacancyCleanupResult.would_delete} vacancies` : "Delete expired/broken vacancies"}
          </Button>
        </div>
        {vacancyCleanupResult && (
          <div className="mt-5 space-y-3" data-testid="vacancy-cleanup-result">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { l: "Scanned", v: vacancyCleanupResult.scanned },
                { l: "Would delete", v: vacancyCleanupResult.would_delete },
                { l: "Kept", v: vacancyCleanupResult.kept },
                { l: "Actually deleted", v: vacancyCleanupResult.deleted },
              ].map((s) => (
                <div key={s.l} className="bg-paper border border-border rounded p-3">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{s.l}</div>
                  <div className="font-mono font-bold text-ink text-2xl mt-1">{s.v}</div>
                </div>
              ))}
            </div>
            {vacancyCleanupResult.preview && vacancyCleanupResult.preview.length > 0 && (
              <div className="bg-paper border border-border rounded-lg p-4">
                <div className="font-semibold text-ink text-sm mb-2">Vacancy cleanup preview ({vacancyCleanupResult.preview.length})</div>
                <ul className="text-xs font-mono space-y-1 max-h-64 overflow-y-auto">
                  {vacancyCleanupResult.preview.map((p, i) => (
                    <li key={i} className="flex flex-wrap gap-x-2 break-all">
                      <span className="text-rose-700">×</span>
                      <span className="text-ink">{p.title || p.id}</span>
                      <span className="text-muted-foreground">— {p.employer_name} — </span>
                      <span className="text-muted-foreground">{p.apply_url || p.expiry_date}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* === Run history (last 50) === */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="font-display font-bold text-ink text-xl tracking-tight">Recent crawl runs</div>
            <p className="text-sm text-muted-foreground mt-1">Last 50 probes (single URL + sweeps + nightly). Click any row to see full diagnostic.</p>
          </div>
          <Button size="sm" variant="outline" onClick={loadRuns} disabled={runsLoading} data-testid="runs-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${runsLoading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>

        <div className="mt-4 flex items-center gap-3 flex-wrap text-sm">
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="border border-border rounded px-2 py-1.5 text-sm bg-paper" data-testid="runs-filter-type">
            <option value="all">{`All feed types (${runs.length})`}</option>
            {distinctFeedTypes.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <Input
            placeholder="Filter by URL or operator name…"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            className="max-w-md text-sm"
            data-testid="runs-filter-text"
          />
          <span className="text-xs text-muted-foreground">{filteredRuns.length} of {runs.length} shown</span>
        </div>

        <div className="mt-4 bg-paper border border-border rounded-lg overflow-x-auto" data-testid="runs-table">
          <table className="w-full text-xs">
            <thead className="bg-sand text-muted-foreground">
              <tr className="text-left">
                <th className="py-2 px-3 w-8"></th>
                <th className="py-2 px-3">When</th>
                <th className="py-2 px-3">URL</th>
                <th className="py-2 px-3">Feed type</th>
                <th className="py-2 px-3">HTTP</th>
                <th className="py-2 px-3">Found</th>
                <th className="py-2 px-3">Inserted</th>
                <th className="py-2 px-3">Skipped</th>
              </tr>
            </thead>
            <tbody>
              {filteredRuns.length === 0 && (
                <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">No runs match the filters.</td></tr>
              )}
              {filteredRuns.map((r) => {
                const isOpen = expandedRun === r.id;
                return (
                  <Fragment key={r.id}>
                    <tr className="border-t border-border hover:bg-sand/40 cursor-pointer" onClick={() => setExpandedRun(isOpen ? null : r.id)} data-testid={`run-row-${r.id}`}>
                      <td className="py-2 px-3">{isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}</td>
                      <td className="py-2 px-3 font-mono text-[11px] text-muted-foreground whitespace-nowrap">{(r.started_at || r.logged_at || "").slice(0, 16).replace("T", " ")}</td>
                      <td className="py-2 px-3 font-mono text-ink truncate max-w-[280px]"><a href={r.careers_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="hover:underline">{r.careers_url}</a></td>
                      <td className="py-2 px-3"><FeedTypeBadge type={r.feed_type} /></td>
                      <td className="py-2 px-3"><StatusPill status={r.http_status} /></td>
                      <td className="py-2 px-3 font-mono">{r.found ?? 0}</td>
                      <td className="py-2 px-3 font-mono">
                        {r.inserted > 0 ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold"><CheckCircle2 className="w-3 h-3" />{r.inserted}</span>
                        ) : <span className="text-muted-foreground">{r.inserted ?? 0}</span>}
                      </td>
                      <td className="py-2 px-3 font-mono">{r.skipped ?? 0}</td>
                    </tr>
                    {isOpen && (
                      <tr className="border-t border-border bg-sand/40">
                        <td colSpan={8} className="py-3 px-3">
                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <div className="font-semibold text-ink text-xs mb-2">Layers tried</div>
                              <table className="w-full text-[11px] font-mono">
                                <tbody>
                                  {(r.layers || []).map((l, i) => (
                                    <tr key={i} className="border-t border-border">
                                      <td className="py-1 pr-3 text-ink font-semibold">{l.name}</td>
                                      <td className="py-1 pr-3"><StatusPill status={l.status} /></td>
                                      <td className="py-1 pr-3">{l.count ?? 0}</td>
                                      <td className="py-1 text-muted-foreground break-all">
                                        {l.endpoint && <div>endpoint: {l.endpoint}</div>}
                                        {l.matched_pattern && <div>pattern: {l.matched_pattern}</div>}
                                        {l.matched_action && <div>action: {l.matched_action}</div>}
                                        {l.error && <div className="text-rose-700">error: {l.error}</div>}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            <div>
                              <div className="font-semibold text-ink text-xs mb-2">Skip reasons {r.skip_reasons ? `(${Object.keys(r.skip_reasons).length})` : ""}</div>
                              <div className="text-[11px] font-mono max-h-48 overflow-y-auto space-y-1">
                                {Object.entries(r.skip_reasons || {}).slice(0, 25).map(([k, v]) => (
                                  <div key={k} className="break-all"><span className="text-rose-700">{v}</span> — <span className="text-muted-foreground">{k}</span></div>
                                ))}
                                {(!r.skip_reasons || Object.keys(r.skip_reasons).length === 0) && (
                                  <div className="text-muted-foreground">— none —</div>
                                )}
                              </div>
                              {(r.errors || []).length > 0 && (
                                <div className="mt-3">
                                  <div className="font-semibold text-rose-800 text-xs mb-1">Errors</div>
                                  <ul className="text-[11px] font-mono space-y-1 text-rose-900">
                                    {r.errors.map((e, i) => <li key={i}>{e}</li>)}
                                  </ul>
                                </div>
                              )}
                              <div className="mt-3 text-[11px] text-muted-foreground">
                                Employer hint: {r.employer_hint || "—"} · Content-Type: {r.content_type || "—"} · Dry-run: {r.dry_run ? "yes" : "no"}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
